import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useGetFixingIndexMarksQuery } from '@src/providers/AppStateProvider/streamingDataSlice';
import { useFixingIndices } from '@src/providers/FixingIndexProvider';
import { EMPTY_OBJECT } from '@talos/kyoko';

export const OTC_MISSING_DEPENDENCIES_LOCATION = {
  DealProductBuilder: 'dealProductBuilder',
  ManualDealTradeView: 'manualDealTradeView',
} as const;

export type OTCMissingDependenciesLocation =
  (typeof OTC_MISSING_DEPENDENCIES_LOCATION)[keyof typeof OTC_MISSING_DEPENDENCIES_LOCATION];

export type OTCMissingDependencyError = {
  source: string;
  reason: 'loaded-empty' | 'unavailable' | 'not-started';
};

export function useOTCMissingDependencies(): OTCMissingDependencyError[] {
  const { isLoaded, fixingIndicesList } = useFixingIndices();
  const { isLoaded: featureFlagsLoaded, enableOTCSupport, enableIndexMarkSubs } = useFeatureFlag();
  const marksNotStarted = featureFlagsLoaded && !enableOTCSupport && !enableIndexMarkSubs;
  const skipIndexMarksQuery = !featureFlagsLoaded || marksNotStarted;
  const {
    data: marksData,
    isSuccess: marksSuccess,
    isError: marksError,
  } = useGetFixingIndexMarksQuery(EMPTY_OBJECT, { skip: skipIndexMarksQuery });

  const errors: OTCMissingDependencyError[] = [];

  if (isLoaded && fixingIndicesList.length === 0) {
    errors.push({ source: 'Index WS', reason: 'loaded-empty' });
  }

  if (marksNotStarted) {
    errors.push({ source: 'Index Mark WS', reason: 'not-started' });
  } else if (marksError) {
    errors.push({ source: 'Index Mark WS', reason: 'unavailable' });
  } else if (marksSuccess && (marksData?.size ?? 0) === 0) {
    errors.push({ source: 'Index Mark WS', reason: 'loaded-empty' });
  }

  return errors;
}
